import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import { Button, Heading3, Loading } from "../../components";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { camelCase, capitalize, isEmpty } from "lodash";
import { CURRENCY_FORMAT } from "../../utils/formatters";
import { lighten, rgba } from "polished";

import searchImage from "../../assets/search-icon.png";
import assistantBackground from "../../assets/assistant-bg.jpg";
const VISIBLE_FILTERS = ["ageGroups", "locationTypes", "bookable", "maxPrice"];

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1rem;
  padding: 1rem;
  border-radius: 1.4rem;
  background-color: rgb(57, 57, 159);
  background-size: cover;
  background-position: top;
  box-shadow: 0 0 1.9rem -0.25rem rgba(57, 57, 159, 0.7);

  @media (${props => props.theme.tabletScreen}) {
    max-width: 600px;
    width: 100%;
    margin: 1.5rem auto 0;
    border: 0;
  }
`;

const Title = styled(Heading3)`
  color: ${props => props.theme.white};
  text-align: left;
  margin-bottom: 0;
  margin-left: 1rem;
`;

const SearchForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InquiryInput = styled.textarea`
  display: block;
  width: 100%;
  padding: 0.5rem 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: ${props => props.theme.black};
  background-color: ${props => props.theme.white};
  border: 1px solid ${props => (props.hasError ? props.theme.red : props.theme.gray100)};
  border-radius: ${props => props.theme.borderRadius};
  margin-bottom: 1rem;
  font-size: 100%;
  // box-shadow: ${props => props.theme.boxShadowInset};
  box-shadow: 0 0 0.9rem 0 ${props => rgba(props.theme.primary, 0.5)};
  opacity: 0.95;
  height: 2.5rem;
  transition: height 0.25s ease;

  &:focus {
    font-size: 100%;
    outline: none;
    border-color: ${props => props.theme.primary};
    height: 5rem;
  }
`;

const FiltersWrapper = styled.div`
  margin-bottom: 1rem;
  font-size: ${props => props.theme.fontSizes.small};
  width: 100%;
  position: relative;
  border-radius: ${props => props.theme.borderRadius};
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2rem;
    background: linear-gradient(to bottom, rgb(53 39 114), rgba(53, 39, 114, 0) 100%);
    pointer-events: none;
  }
`;

const FiltersWrapperInner = styled.div`
  display: flex;
  flex-direction: column-reverse;
  flex: 1 1 auto;
  padding-top: 1.5rem;

  max-height: ${props => (props.condensed ? "120px" : "400px")};
  overflow-y: auto;
  overflow-x: hidden;
  transition: max-height 0.5s ease;
  transition-delay: 1s;
`;

const Filters = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Filter = styled.li`
  display: flex;
  flex-wrap: wrap;
  margin: 0.5rem 0;
`;

const FilterValueWrapper = styled.div`
  text-align: left;
  opacity: 0.9;
`;

const FilterValue = styled.span`
  margin: 0;
  padding: 0.25rem 0.5rem;
  background-color: ${props => props.theme.info};
  color: ${props => props.theme.white};
  border-radius: ${props => props.theme.borderRadius};
  font-size: ${props => props.theme.fontSizes.small};
  margin: 0.25rem;
  white-space: nowrap;
  box-shadow: 0 0 0.5rem 0 ${props => props.theme.info};
`;

const TermValue = styled(FilterValue)`
  background-color: ${props => props.theme.primary};
  box-shadow: 0 0 0.5rem 0 ${props => props.theme.primary};
`;

const InquiryRequest = styled.p`
  margin-bottom: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${props => props.theme.borderRadius};
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.black};
  font-style: italic;
  text-align: right;
  max-width: 60%;
  margin-left: auto;
  background-color: ${props => props.theme.light};
`;

const InquiryResponse = styled.div`
  margin-top: 1rem;
  text-align: left;
  max-width: 60%;
`;

const Message = styled.div`
  margin: 0 0 0.5rem 0;
  max-width: 85%;
  color: ${props => props.theme.white};

  @media (${props => props.theme.tabletScreen}) {
    max-width: 60%;
  }
`;

const Answer = styled(Message)`
  padding: 0.5rem 1rem;
  background-color: ${props => lighten(0.25, props.theme.primary)};
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.black};
`;

const ContextWrapper = styled.section`
  margin: 1rem 0 1.5rem 1rem;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SearchIcon = styled.img`
  width: 100%;
  height: auto;
  max-width: 100px;
  min-width: 60px;
  display: inline-block;
  margin: 0.25rem auto;

  @media (${props => props.theme.tabletScreen}) {
    max-width: 200px;
  }
`;

const TitleWrapper = styled.header`
  display: flex;
  flex-direction: row;
  align-items: center;

  @media (${props => props.theme.tabletScreen}) {
  }

  ${SearchIcon} {
    max-width: 60px;

    @media (${props => props.theme.tabletScreen}) {
      max-width: 100px;
    }
  }
`;

const filterContent = (filterKey, filters) => {
  const filter = filters[filterKey];

  switch (filterKey) {
    case "ageGroups":
      return (
        <FilterValueWrapper>
          {filter.map(ageGroup => (
            <FilterValue key={ageGroup}>
              <FormattedMessage id={`pages.Search.filters.age${capitalize(ageGroup)}`} />
            </FilterValue>
          ))}
        </FilterValueWrapper>
      );
    case "locationTypes":
      return (
        <FilterValueWrapper>
          {filter.map(locationType => (
            <FilterValue key={locationType}>
              <FormattedMessage id={`course.attributes.locationType.${camelCase(locationType)}`} />
            </FilterValue>
          ))}
        </FilterValueWrapper>
      );
    case "bookable":
      return (
        <FilterValueWrapper>
          {filter ? (
            <FilterValue>
              <FormattedMessage id="pages.Search.filters.bookable" />
            </FilterValue>
          ) : (
            <FilterValue>
              <FormattedMessage id="pages.Search.filters.all" />
            </FilterValue>
          )}
        </FilterValueWrapper>
      );
    case "maxPrice":
      return filter && filter > 0 ? (
        <FilterValueWrapper>
          <FilterValue>
            <FormattedMessage id="pages.Search.filters.maxPrice" />{" "}
            <FormattedNumber value={filter} {...CURRENCY_FORMAT} maximumFractionDigits={0} />
          </FilterValue>
        </FilterValueWrapper>
      ) : null;
    default:
      return null;
  }
};

const SearchAssistant = ({
  isLoading,
  onSubmit,
  intl,
  term,
  filters,
  setFilters,
  searchByText,
  changeAddress,
  address,
}) => {
  const [thinking, setThinking] = useState(false);
  const [failed, setFailed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [query, setQuery] = useState("");
  const [inputText, setInputText] = useState(query || "");
  const [answer, setAnswer] = useState("");
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setExpanded(!succeeded || failed);
  }, [succeeded, failed]);

  const getSearchApiParams = async e => {
    e.preventDefault();

    if (!inputText.trim()) {
      return;
    }

    setFailed(false);
    setSucceeded(false);
    setThinking(true);
    setQuery(inputText);

    try {
      const response = await searchByText(inputText);
      const newFilters = {
        ageGroups: response.ageGroups || [],
        locationTypes: response.locationTypes || [],
        bookable: response.bookable || true,
        maxPrice: response.maxPrice,
        radius: response.radius || 25,
        startDate: response.startDate || null,
      };

      setFilters({
        ...filters,
        ...newFilters,
      });
      changeAddress(response.address, response.lat, response.lng);

      setAnswer(response.message || intl.formatMessage({ id: "pages.Search.assistedSuccess" }));
      onSubmit(response.term);
      setInputText("");
      setSucceeded(true);
    } catch (error) {
      setFailed(true);

      if (error.response) {
        const { response } = error;
        switch (response.status) {
          case 422:
            setAnswer(
              response?.data?.message ||
                intl.formatMessage({ id: "pages.Search.assistantUnprocessableEntity" }),
            );
            break;
          case 429:
            setAnswer(
              response?.data?.message ||
                intl.formatMessage({ id: "pages.Search.assistantRateLimit" }),
            );
            break;
          default:
            setAnswer(
              response?.data?.message || intl.formatMessage({ id: "pages.Search.assistantError" }),
            );
            break;
        }
      }
    } finally {
      setThinking(false);
    }
  };

  return (
    <Wrapper style={{ backgroundImage: `url(${assistantBackground})` }}>
      <TitleWrapper>
        <SearchIcon src={searchImage} />
        <Title>
          <FormattedMessage id="pages.Search.assistedTitle" />
        </Title>
      </TitleWrapper>
      {!answer && !query && (
        <FiltersWrapper>
          <FiltersWrapperInner>
            <Answer>
              <FormattedMessage id="pages.Search.assistedIntro" />
            </Answer>
          </FiltersWrapperInner>
        </FiltersWrapper>
      )}
      {query && (
        <FiltersWrapper>
          <FiltersWrapperInner condensed={!expanded}>
            {answer && !thinking && <Answer>{answer}</Answer>}
            {thinking ? (
              <LoadingWrapper>
                <Loading small transparent />
              </LoadingWrapper>
            ) : (
              <>
                {!failed && (
                  <InquiryResponse>
                    <Answer>
                      <FormattedMessage id="pages.Search.assistedAnswer" />
                    </Answer>
                    <ContextWrapper>
                      <Message>
                        <Filters>
                          {Object.keys(filters)
                            .filter(
                              filterKey =>
                                VISIBLE_FILTERS.includes(filterKey) && !isEmpty(filters[filterKey]),
                            )
                            .map(filterKey => (
                              <Filter name={filterKey} key={filterKey}>
                                {filterContent(filterKey, filters)}
                              </Filter>
                            ))}
                        </Filters>
                      </Message>
                      {address && (
                        <Message>
                          <FormattedMessage id="pages.Search.address" />
                          <FilterValueWrapper>
                            <FilterValue>{address}</FilterValue>
                          </FilterValueWrapper>
                        </Message>
                      )}

                      {term && (
                        <Message>
                          <FormattedMessage id="pages.Search.assistedTerm" />
                          <FilterValueWrapper>
                            <TermValue>{term}</TermValue>
                          </FilterValueWrapper>
                        </Message>
                      )}
                    </ContextWrapper>
                  </InquiryResponse>
                )}
              </>
            )}
            <InquiryRequest>{query}</InquiryRequest>
          </FiltersWrapperInner>
        </FiltersWrapper>
      )}
      <SearchForm onSubmit={getSearchApiParams}>
        <InquiryInput
          name="search"
          placeholder={intl.formatMessage({ id: "pages.SearchMap.text" })}
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey && "form" in e.target) {
              e.preventDefault();
              e.target.form.requestSubmit();
            }
          }}
          onFocus={() => setExpanded(true)}
        />
        <Button color="primary" type="submit" disabled={isLoading || thinking || !inputText.trim()}>
          {isLoading
            ? intl.formatMessage({ id: "pages.Search.loading" })
            : intl.formatMessage({ id: "pages.Search.submit" })}
        </Button>
      </SearchForm>
    </Wrapper>
  );
};

export default SearchAssistant;
